import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../../context/AppContext';
import api from '../../../services/api';

const ConnectSlackStep = ({ onNext, onSkip, onBack }) => {
  const { currentUser } = useAppContext();
  const [isConnected, setIsConnected] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Check for OAuth callback first
    const params = new URLSearchParams(window.location.search);
    if (params.get('slack_connected') === 'true') {
      setIsConnected(true);
      setIsChecking(false);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      return; // Skip API call - we already know it's connected
    }

    // Only check connection status if no OAuth callback
    checkSlackConnection();
  }, []);

  const checkSlackConnection = async () => {
    try {
      const response = await api.get('/admin/settings');
      setIsConnected(response.data.slack_enabled && response.data.slack_team_id);
    } catch (error) {
      console.error('Error checking Slack connection:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const handleConnectSlack = () => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.apprecio.app';
    if (currentUser && currentUser.company_id) {
      // Pass redirect_to so OAuth returns to onboarding wizard instead of admin settings
      window.location.href = `${API_BASE_URL}/api/v1/slack/oauth/authorize?company_id=${currentUser.company_id}&redirect_to=/setup/onboarding`;
    }
  };

  if (isChecking) {
    return <div className="text-center">Checking Slack connection...</div>;
  }

  return (
    <div className="text-center">
      <h2 className="text-3xl font-bold text-gray-900 mb-4">Connect Your Slack Workspace</h2>
      <p className="text-lg text-gray-600 mb-8">
        Enable your team to send appreciations directly from Slack
      </p>

      {isConnected ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
          <div className="text-green-600 text-5xl mb-4">✓</div>
          <p className="text-lg font-semibold text-green-800">Slack Connected Successfully!</p>
          <p className="text-gray-600 mt-2">Your team can now use Apprecio in Slack</p>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 mb-8">
          <img
            src="https://a.slack-edge.com/80588/marketing/img/meta/slack_hash_256.png"
            alt="Slack"
            className="w-16 h-16 mx-auto mb-4"
          />
          <p className="text-gray-700 mb-6">
            Connect Slack to enable appreciation sending, reactions, and slash commands
          </p>
          <button
            onClick={handleConnectSlack}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            Connect to Slack
          </button>
        </div>
      )}

      <div className="flex justify-center space-x-4">
        {onBack && (
          <button
            onClick={onBack}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Back
          </button>
        )}
        {isConnected && (
          <button
            onClick={onNext}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            Continue
          </button>
        )}
        <button
          onClick={onSkip}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
        >
          {isConnected ? 'Skip User Import' : 'Skip Slack Setup'}
        </button>
      </div>
    </div>
  );
};

export default ConnectSlackStep;
